/**
 * Workspace switcher (ADR-0002, CLAUDE.md 11.1).
 *
 * Renders **nothing** when the user belongs to one workspace, which for a sole
 * practitioner is always. That is the whole point of the personal-workspace model:
 * "a solo engineer never sees the word *workspace*". A switcher with one entry is
 * precisely the complexity the model exists to avoid.
 *
 * Which leaves a gap this closes: with the switcher hidden there was nowhere at all
 * to create a second workspace, so the personal workspace was a dead end rather than
 * a starting point (11.5). The "create an organisation" item lives at the foot of
 * this menu for anyone who already has two, and on the settings General section for
 * anyone who does not -- exactly the case where this component renders nothing.
 */
import { useState } from 'react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { Building2, Check, ChevronDown, Plus, User } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useAuth } from '@/auth/AuthProvider'
import { CreateOrganisationDialog } from '@/components/workspace/CreateOrganisationDialog'
import { cn } from '@/lib/cn'

export function WorkspaceSwitcher() {
  const { t } = useTranslation()
  const { workspaces, workspaceId, switchWorkspace } = useAuth()
  const [creating, setCreating] = useState(false)

  const active = workspaces.find((w) => w.id === workspaceId)

  if (workspaces.length <= 1) return null

  return (
    <>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger
          aria-label={t('workspace.switch')}
          className="flex h-11 max-w-[12rem] items-center gap-1.5 rounded-md px-2 text-sm text-text-muted hover:bg-surface-raised hover:text-text"
        >
          {active?.is_personal ? (
            <User size={15} aria-hidden />
          ) : (
            <Building2 size={15} aria-hidden />
          )}
          <span className="truncate">
            {active?.is_personal ? t('workspace.personal') : (active?.name ?? '')}
          </span>
          <ChevronDown size={14} aria-hidden />
        </DropdownMenu.Trigger>

        <DropdownMenu.Portal>
          <DropdownMenu.Content
            align="start"
            sideOffset={6}
            className="z-50 min-w-[14rem] rounded-md border border-border bg-surface-raised p-1 shadow-[var(--shadow-md)]"
          >
            <DropdownMenu.Label className="px-2 py-1.5 text-xs text-text-muted">
              {t('workspace.label')}
            </DropdownMenu.Label>
            {workspaces.map((workspace) => (
              <DropdownMenu.Item
                key={workspace.id}
                onSelect={() => switchWorkspace(workspace.id)}
                className={cn(
                  'flex cursor-pointer items-center gap-2 rounded px-2 py-2 text-sm outline-none',
                  'data-[highlighted]:bg-surface data-[highlighted]:text-text',
                )}
              >
                {workspace.is_personal ? (
                  <User size={14} aria-hidden />
                ) : (
                  <Building2 size={14} aria-hidden />
                )}
                <span className="flex-1 truncate">
                  {workspace.is_personal ? t('workspace.personal') : workspace.name}
                </span>
                {workspace.id === workspaceId && <Check size={14} aria-hidden />}
              </DropdownMenu.Item>
            ))}

            <DropdownMenu.Separator className="my-1 h-px bg-border" />

            <DropdownMenu.Item
              onSelect={() => setCreating(true)}
              className={cn(
                'flex cursor-pointer items-center gap-2 rounded px-2 py-2 text-sm outline-none',
                'data-[highlighted]:bg-surface data-[highlighted]:text-text',
              )}
            >
              <Plus size={14} aria-hidden />
              <span className="flex-1 truncate">{t('workspace.create_org')}</span>
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>

      <CreateOrganisationDialog open={creating} onOpenChange={setCreating} />
    </>
  )
}
